import { computed } from 'mobx'
import { orderBy } from 'lodash-es'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  type JobProgression,
  type JobProgressionFlow,
  type JobProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'
import { type Member } from '@/features/iam/members/Member'
import type { Id } from '@/infra/id'
import { dateFormatters } from '@/utils/date-util'
import { memberRoleLabel } from '@/features/jobs/details/member-role-label'

export interface JobProgressionHistoryActor {
  member: Member
  roleLabel?: string
}

export interface JobProgressionHistoryItem {
  id: Id
  title: string
  formattedDateCreated: string | null
  createdBy: JobProgressionHistoryActor | null
}

/**
 * Dialog listing every progression step for the job as a timeline.
 */
export class JobProgressionHistoryViewModel {
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _getProgression: () => JobProgression | undefined,
    private readonly _getStepTitle: (
      flow: JobProgressionFlow,
      status: JobProgressionStepStatus,
    ) => string,
    private readonly _resolveMember: (userPublicId: string) => Member,
  ) {}

  @computed
  get items(): JobProgressionHistoryItem[] {
    const progression = this._getProgression()
    if (!progression) {
      return []
    }

    const flow = progression.progressionFlow
    return orderBy(progression.steps, ['dateCreated'], ['desc']).map(
      (step) => ({
        id: step.id,
        title: this._getStepTitle(flow, step.status),
        formattedDateCreated: step.dateCreated
          ? dateFormatters.dateLongWithTime.format(step.dateCreated)
          : null,
        createdBy: this._toActor(step.createdByUserPublicId),
      }),
    )
  }

  private _toActor(userPublicId?: string): JobProgressionHistoryActor | null {
    if (!userPublicId) {
      return null
    }

    const member = this._resolveMember(userPublicId)
    return {
      member,
      roleLabel: memberRoleLabel(member),
    }
  }
}
